import { safeGtag, trackAddToCart as gaTrackAddToCart, trackViewItem } from "@/lib/analytics";
import {
  resolveEventId,
  resolveTicketId,
  withEventContext,
  withTicketContext,
  buildEventItem,
  buildTicketItem,
} from "@/lib/analytics/helpers";

const trackedPurchases = new Set();
const isBrowser = () => typeof window !== "undefined";

const getFbq = () => {
  if (isBrowser() && typeof window.fbq === "function") {
    return window.fbq;
  }
  return null;
};

// ---------------------------------------------------------------------------
// ViewContent  →  GA4: view_item
// ---------------------------------------------------------------------------
export const trackViewContent = ({
  eventId,
  eventName,
  category,
  price,
  currency = "INR",
  city,
  start_date,
  venue,
}) => {
  const event_id = resolveEventId(eventId);

  try {
    const fbq = getFbq();
    if (fbq && event_id) {
      fbq("track", "ViewContent", {
        content_ids: [event_id],
        content_name: eventName || "Event",
        content_type: "product",
        content_category: category || undefined,
        value: typeof price === "number" && !isNaN(price) ? price : undefined,
        currency: currency || "INR",
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] ViewContent tracking failed:", err);
  }

  trackViewItem({
    id: event_id,
    name: eventName,
    price,
    currency,
    category,
    start_date,
    address: city ? { city } : undefined,
    venue,
  });
};

// ---------------------------------------------------------------------------
// AddToCart  →  GA4: add_to_cart
// ---------------------------------------------------------------------------
export const trackAddToCart = ({
  eventId,
  eventName,
  ticketId,
  ticketName,
  quantity = 1,
  price = 0,
  currency = "INR",
}) => {
  const numQty = Number(quantity) || 1;
  const unitPrice = Number(price) || 0;
  const totalValue = numQty * unitPrice;
  const event_id = resolveEventId(eventId);
  const ticket_id = resolveTicketId(ticketId);

  try {
    const fbq = getFbq();
    if (fbq) {
      fbq("track", "AddToCart", {
        content_ids: [ticket_id || event_id || "ticket"].filter(Boolean),
        content_name: ticketName || "Ticket",
        content_type: "product",
        currency: currency || "INR",
        value: totalValue,
        num_items: numQty,
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] AddToCart tracking failed:", err);
  }

  gaTrackAddToCart({
    event_id,
    event_name: eventName,
    ticket_id,
    ticket_name: ticketName,
    quantity: numQty,
    price: unitPrice,
    currency,
  });
};

// ---------------------------------------------------------------------------
// InitiateCheckout  →  GA4: begin_checkout
// ---------------------------------------------------------------------------
export const trackInitiateCheckout = ({
  eventId,
  eventName,
  ticketId,
  ticketName,
  ticketCount = 1,
  totalAmount = 0,
  currency = "INR",
}) => {
  const numItems = Number(ticketCount) || 1;
  const value = Number(totalAmount) || 0;
  const event_id = resolveEventId(eventId);
  const ticket_id = resolveTicketId(ticketId);

  try {
    const fbq = getFbq();
    if (fbq) {
      fbq("track", "InitiateCheckout", {
        content_ids: event_id ? [event_id] : undefined,
        content_name: eventName || undefined,
        content_type: "product",
        value: value,
        currency: currency || "INR",
        num_items: numItems,
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] InitiateCheckout tracking failed:", err);
  }

  const itemRow = buildTicketItem({
    ticket_id,
    ticket_name: ticketName,
    event_id,
    price: numItems > 0 ? value / numItems : value,
    quantity: numItems,
  });

  safeGtag(
    "begin_checkout",
    withTicketContext(
      withEventContext(
        {
          currency: currency || "INR",
          value: value,
          quantity: numItems,
          items: [itemRow],
        },
        { event_id, event_name: eventName },
      ),
      { ticket_id, ticket_name: ticketName },
    ),
  );
};

// ---------------------------------------------------------------------------
// Purchase  →  GA4: purchase
// ---------------------------------------------------------------------------
export const trackPurchase = ({
  orderId,
  eventId,
  eventName,
  ticketId,
  ticketName,
  ticketCount = 1,
  value = 0,
  currency = "INR",
}) => {
  if (!orderId) return;

  const orderKey = String(orderId);
  if (trackedPurchases.has(orderKey)) return;

  if (isBrowser()) {
    try {
      const stored = sessionStorage.getItem("tykkit_pixel_purchases");
      const parsed = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed) && parsed.includes(orderKey)) {
        trackedPurchases.add(orderKey);
        return;
      }
      parsed.push(orderKey);
      sessionStorage.setItem("tykkit_pixel_purchases", JSON.stringify(parsed));
    } catch {
      // Ignore storage access errors
    }
  }

  trackedPurchases.add(orderKey);

  const confirmedValue = Number(value) || 0;
  const numItems = Number(ticketCount) || 1;
  const event_id = resolveEventId(eventId);
  const ticket_id = resolveTicketId(ticketId);

  try {
    const fbq = getFbq();
    if (fbq) {
      fbq("track", "Purchase", {
        content_ids: event_id ? [event_id] : undefined,
        content_name: eventName || undefined,
        content_type: "product",
        value: confirmedValue,
        currency: currency || "INR",
        num_items: numItems,
        order_id: orderKey,
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] Purchase tracking failed:", err);
  }

  safeGtag(
    "purchase",
    withTicketContext(
      withEventContext(
        {
          transaction_id: orderKey,
          currency: currency || "INR",
          value: confirmedValue,
          items: [
            buildTicketItem({
              ticket_id,
              ticket_name: ticketName,
              event_id,
              price: numItems > 0 ? confirmedValue / numItems : confirmedValue,
              quantity: numItems,
            }),
          ],
        },
        { event_id, event_name: eventName },
      ),
      { ticket_id, ticket_name: ticketName },
    ),
  );
};

// ---------------------------------------------------------------------------
// CompleteRegistration  →  GA4: sign_up
// ---------------------------------------------------------------------------
export const trackCompleteRegistration = ({ method = "otp" } = {}) => {
  try {
    const fbq = getFbq();
    if (fbq) {
      fbq("track", "CompleteRegistration", {
        content_name: "Signup",
        status: true,
        registration_method: method,
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] CompleteRegistration tracking failed:", err);
  }

  safeGtag("sign_up", { method });
};

// ---------------------------------------------------------------------------
// AddPaymentInfo  →  GA4: add_payment_info
// ---------------------------------------------------------------------------
export const trackAddPaymentInfo = ({
  eventId,
  eventName,
  ticketId,
  ticketName,
  value = 0,
  currency = "INR",
}) => {
  const event_id = resolveEventId(eventId);
  const ticket_id = resolveTicketId(ticketId);

  try {
    const fbq = getFbq();
    if (fbq) {
      fbq("track", "AddPaymentInfo", {
        content_ids: event_id ? [event_id] : undefined,
        content_name: eventName || undefined,
        content_type: "product",
        value: Number(value) || 0,
        currency: currency || "INR",
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] AddPaymentInfo tracking failed:", err);
  }

  safeGtag(
    "add_payment_info",
    withTicketContext(
      withEventContext(
        {
          currency: currency || "INR",
          value: Number(value) || 0,
          items: [
            buildTicketItem({
              ticket_id,
              ticket_name: ticketName,
              event_id,
              price: Number(value) || 0,
              quantity: 1,
            }),
          ],
        },
        { event_id, event_name: eventName },
      ),
      { ticket_id, ticket_name: ticketName },
    ),
  );
};

// ---------------------------------------------------------------------------
// Search  →  GA4: search
// ---------------------------------------------------------------------------
export const trackSearch = ({ searchQuery }) => {
  try {
    const fbq = getFbq();
    if (fbq && searchQuery) {
      fbq("track", "Search", {
        search_string: String(searchQuery).trim(),
      });
    }
  } catch (err) {
    console.warn("[Meta Pixel] Search tracking failed:", err);
  }

  if (searchQuery) {
    safeGtag("search", { search_term: String(searchQuery).trim() });
  }
};

// ---------------------------------------------------------------------------
// Custom event  →  GA4: custom event (same name)
// ---------------------------------------------------------------------------
export const trackCustomEvent = (eventName, params = {}) => {
  try {
    const fbq = getFbq();
    if (fbq && eventName) {
      fbq("trackCustom", eventName, params);
    }
  } catch (err) {
    console.warn(`[Meta Pixel] Custom event "${eventName}" failed:`, err);
  }

  if (eventName) {
    safeGtag(eventName, params);
  }
};
